import React from 'react';
import { LandingComponent, SubLandingComponent } from 'app/types';
import Text from 'common/components/Text/Text';

import { Button } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { formatMonth } from 'common/utils/conversions';
import { useMediaQuery } from 'react-responsive';
import styles from '../LandingForm.module.scss';

interface PreviewComponentProps {
  landing_cmp: LandingComponent;
}

interface MainWebinarProps {
  webinar: SubLandingComponent;
}

const MainWebinarComponent = ({ webinar }: MainWebinarProps): React.ReactElement => {
  const webinarDate = new Date(webinar.config.datetime);
  function padWithZero(number: number) {
    return number < 10 ? `0${number}` : number;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        backgroundImage: `url(${webinar.image_file})`,
        backgroundSize: 'cover',
        minHeight: '450px',
        padding: '43px 33px 46px 28px',
        borderRadius: '10px',
      }}
    >
      <div style={{ display: 'flex', gap: '16px' }}>
        <div
          style={{
            background: '#297DF2',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            fontSize: '16px',
            fontWeight: '600',
            borderRadius: '5px',
            width: '72px',
            height: '72px',
          }}
        >
          <span>{webinarDate.getDate()}</span>
          <span>{formatMonth(webinarDate)}</span>
        </div>
        <Text size={20} weight={600} inlineStyle={{ color: 'white' }}>
          {webinar.config.webinar_title}
          <Text size={14} weight={500} inlineStyle={{ color: 'white' }}>{`${padWithZero(
            webinarDate.getHours()
          )}:${padWithZero(webinarDate.getMinutes())} ${webinar.config.datetime_timezone}`}</Text>
        </Text>
      </div>
      <Button
        href={webinar.config.webinar_link}
        style={{ borderRadius: '50px', alignSelf: 'flex-end' }}
      >
        Register now
      </Button>
    </div>
  );
};

const WebinarCard = ({ webinar }: MainWebinarProps): React.ReactElement => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const webinarDate = new Date(webinar.config.datetime);
  function padWithZero(number: number) {
    return number < 10 ? `0${number}` : number;
  }
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: !isMobile ? 'space-between' : 'flex-start',
        padding: !isMobile ? '20px 30px' : '10px 15px',
        borderRadius: '10px',
        background: '#E8F3FE',
        gap: isMobile ? '12px' : 'inherit',
      }}
    >
      <div
        style={{
          background: '#297DF2',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          color: 'white',
          fontSize: !isMobile ? '16px' : '10px',
          fontWeight: '600',
          borderRadius: '5px',
          width: !isMobile ? '64px' : '44px',
          height: !isMobile ? '64px' : '44px',
        }}
      >
        <span>{webinarDate.getDate()}</span>
        <span>{formatMonth(webinarDate)}</span>
      </div>
      <Text
        size={isMobile ? 14 : 16}
        weight={600}
        inlineStyle={{ color: '#193676', maxWidth: '55%', paddingLeft: '10px' }}
      >
        {webinar.config.webinar_title}
        <Text
          size={isMobile ? 12 : 14}
          weight={500}
          inlineStyle={{ color: '#727D9B' }}
        >{`${padWithZero(webinarDate.getHours())}:${padWithZero(webinarDate.getMinutes())} ${
          webinar.config.datetime_timezone
        }`}</Text>
        {isMobile && (
          <Button
            href={webinar.config.webinar_link}
            style={{
              borderRadius: '50px',
              alignSelf: 'flex-end',
              marginTop: '16px',
            }}
          >
            Register
          </Button>
        )}
      </Text>
      {!isMobile && (
        <Button
          href={webinar.config.webinar_link}
          style={{ borderRadius: '50px', alignSelf: 'flex-end' }}
        >
          Register now
        </Button>
      )}
    </div>
  );
};

const WebinarPreview = ({ landing_cmp }: PreviewComponentProps): React.ReactElement => {
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const allWebinarsHaveDate = (webinars: SubLandingComponent[]) => {
    return webinars.every((item) => item.config.datetime);
  };

  const sortedWebinarsByDate = (webinars: SubLandingComponent[]) => {
    if (allWebinarsHaveDate(webinars)) {
      return [...webinars].sort(
        (a, b) => new Date(a.config.datetime).getTime() - new Date(b.config.datetime).getTime()
      );
    }
    return webinars;
  };
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: landing_cmp.size !== 'sm' && landing_cmp.size !== 'md' ? landing_cmp.size : 'auto',
        padding: landing_cmp.size === 'sm' ? '0 12rem' : '0 4vw',
        margin: landing_cmp.gap,
        background: !(landing_cmp.background instanceof File) ? landing_cmp.background : 'auto',
        backgroundSize: landing_cmp.background_size,
        backgroundImage: `url(${landing_cmp.background_image})`,
      }}
      className={landing_cmp.class_name}
      key={landing_cmp.id}
    >
      {(landing_cmp.config.title || landing_cmp.config.section_title) && (
        <div
          className="headerSection"
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        >
          {landing_cmp.config.section_title && (
            <div className={`${styles.sectionTitle} text-center d-flex flex-column`}>
              {landing_cmp.config.section_title}
              <div
                style={{ width: '87px', height: '8px', background: '#E8F3FE', alignSelf: 'center' }}
              ></div>
            </div>
          )}
          {landing_cmp.config.title && (
            <div
              style={{
                fontSize: '40px',
                color: '#193676',
                fontWeight: '700',
                textAlign: 'center',
                marginBottom: '44px',
              }}
            >
              {landing_cmp.config.title}
            </div>
          )}
          {landing_cmp.config.description && (
            <Text size={16} weight={400} inlineStyle={{ textAlign: 'center', maxWidth: '65%' }}>
              <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                {landing_cmp.config.description}
              </ReactMarkdown>
            </Text>
          )}
        </div>
      )}
      <div className={`row w-100 mt-5`}>
        <div className="col" style={{ marginBottom: isMobile ? '24px' : 'auto' }}>
          <MainWebinarComponent
            webinar={sortedWebinarsByDate(landing_cmp.sub_landing_components)[0]}
          />
        </div>

        <div
          className="col"
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-around',
            gap: isMobile ? '24px' : 'inherit',
          }}
        >
          {sortedWebinarsByDate(landing_cmp.sub_landing_components)
            .slice(1)
            .map((webinar) => (
              <WebinarCard webinar={webinar} key={webinar.id} />
            ))}
        </div>
      </div>
    </div>
  );
};

export default WebinarPreview;
